import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    FetchInventoryActivityLogsInput,
    InventoryActivityLogEntry,
    InventoryActivityLogResponse,
    InventoryExportHistoryEntry,
    InventoryExportHistoryResponse,
    InventoryExportMode,
} from '../../types/inventory';
import { getInventoryActorName } from '../../components/inventory/inventoryActivityLogShared';

const getInventoryActivityLogs = async (
    sessionId: string,
    input: FetchInventoryActivityLogsInput,
): Promise<InventoryActivityLogResponse> => {
    const { data } = await axios.post(`${API_URL}/inventory/activity-logs`, input, {
        headers: { Authorization: sessionId },
    });
    return data;
};

function mapExportLog(log: InventoryActivityLogEntry): InventoryExportHistoryEntry | null {
    const meta = log.metadata ?? {};
    const exportId = meta.exportId;
    if (!exportId) return null;
    const mode = (meta.mode === 'all' || meta.mode === 'filtered' ? meta.mode : 'filtered') as InventoryExportMode;
    const rowCount = Number(meta.rowCount ?? 0);
    return {
        exportId,
        createdAt: log.timestamp,
        mode,
        rowCount: Number.isFinite(rowCount) ? rowCount : 0,
        filename: meta.filename || undefined,
        userName: getInventoryActorName(log),
    };
}

export const listExportHistory = async (
    sessionId: string,
    limit = 50,
): Promise<InventoryExportHistoryResponse> => {
    const data = await getInventoryActivityLogs(sessionId, {
        all: true,
        page: 1,
        limit,
        eventTypes: ['inventory-exported'],
    });
    const exports = data.logs
        .map(mapExportLog)
        .filter((e): e is InventoryExportHistoryEntry => e != null);
    return { exports };
};

export const useInventoryExportHistory = (enabled = true, limit = 50) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: [
            '/inventory/activity-logs',
            'inventory-exported',
            sessionContext?.organizationId ?? '',
            limit,
        ],
        queryFn: () => listExportHistory(sessionContext?.sessionId ?? '', limit),
        enabled: enabled && !!sessionContext?.sessionId,
    });
};
